import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { Footer } from '../components/MoreSections';
import PropertyCard from '../components/PropertyCard';
import { listProperties, listDevelopers, listCities } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Loader2, Search, Home, ChevronRight, X } from 'lucide-react';

const TYPE_LABELS = {
  resale: 'Resale Properties',
  rent: 'Rent Properties',
  resale_commercial: 'Resale Commercial',
  rent_commercial: 'Rent Commercial',
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const ListingsPage = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const type = query.get('type') || 'resale';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [developers, setDevelopers] = useState([]);

  const [city, setCity] = useState(query.get('city') || 'all');
  const [builder, setBuilder] = useState(query.get('builder') || 'all');
  const [bhk, setBhk] = useState(query.get('bhk') || 'all');
  const [q, setQ] = useState(query.get('q') || '');
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    listCities().then(setCities).catch(() => {});
    listDevelopers().then(setDevelopers).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { type, limit: 60 };
    if (city !== 'all') params.city = city;
    if (builder !== 'all') params.builder = builder;
    if (bhk !== 'all') params.bhk = bhk;
    if (q) params.q = q;
    listProperties(params)
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [type, city, builder, bhk, q]);

  const updateType = (t) => {
    const params = new URLSearchParams();
    params.set('type', t);
    navigate(`/listings?${params.toString()}`);
  };

  const bhkOptions = ['all', '1 BHK', '2 BHK', '2.5 BHK', '3 BHK', '3.5 BHK', '4 BHK', 'Retail', 'Food Court'];
  const hasFilters = city !== 'all' || builder !== 'all' || bhk !== 'all' || q;

  const clearFilters = () => { setCity('all'); setBuilder('all'); setBhk('all'); setQ(''); setSearchInput(''); };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
            <Link to="/" className="hover:text-[#9a1b2f] flex items-center gap-1"><Home className="w-3 h-3" /> Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium">{TYPE_LABELS[type]}</span>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-900">{TYPE_LABELS[type]}</h1>
              <p className="text-xs text-gray-500 mt-1">{loading ? 'Loading...' : `${total} verified ${total === 1 ? 'property' : 'properties'} found`}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => updateType(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${type === k ? 'bg-[#9a1b2f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Filters */}
        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">Filters</h3>
              {hasFilters && (
                <button onClick={clearFilters} className="text-xs text-[#9a1b2f] hover:underline flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); setQ(searchInput); }} className="mb-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search project, location..." className="h-9 pl-8 text-sm" />
              </div>
            </form>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">City</label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">Builder</label>
                <Select value={builder} onValueChange={setBuilder}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All Builders</SelectItem>
                    {developers.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">Configuration</label>
                <Select value={bhk} onValueChange={setBhk}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {bhkOptions.map((o) => <SelectItem key={o} value={o}>{o === 'all' ? 'All Configurations' : o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white rounded-2xl p-5">
            <h4 className="font-bold">Can't find your dream home?</h4>
            <p className="text-xs opacity-90 mt-1">Post your requirement and we'll match you with the best options.</p>
            <Button className="mt-3 w-full bg-white text-[#9a1b2f] hover:bg-gray-50" size="sm">Post Requirement</Button>
          </div>
        </aside>

        {/* Grid */}
        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">No properties match your filters</h3>
              <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or clear them to see all listings.</p>
              <Button className="mt-4 bg-[#9a1b2f] hover:bg-[#7a1524]" onClick={clearFilters}>Clear Filters</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ListingsPage;
